import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, UserX, UserCheck, X, Search, KeyRound, ChevronDown } from 'lucide-react';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import PermissionCheckboxes from './PermissionCheckboxes';
import ResultCount from '../shared/ResultCount';
import { formatDateTime } from '../../utils/dateFormat';
import { fetchJSON } from '../../utils/api';
import { inputCls, LockTip, tooltipPopupCls } from '../shared/FormKit';
import { useAuth } from '../../contexts/AuthContext';

// Accounts (docs/modules/auth-admin.md §5, §8). An account carries one or
// more roles (GET /api/admin/roles, managed at /settings/roles); its
// permissions are the live union of those roles' defaults plus the extras
// ticked here for this account alone. Role-given keys show locked in the
// tick-list — they follow the role, and changing the role changes them.
// The hidden system account is not in GET /api/admin/users, so every row here
// is an ordinary account with one or more roles.

// Filter dropdown values are the ones actually present on the accounts, not the
// full managed lists — picking an option always leaves rows on screen.
const distinctValues = (users, key) =>
  [...new Set(users.map(u => u[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b));

const rolesOf = (u) => (u.roles && u.roles.length ? u.roles : (u.role ? [{ key: u.role, name: u.role }] : []));

// An account can carry many roles; printing them all makes one row as tall as
// the five chips it holds. The table prints the first two and folds the rest
// into a "+N more" chip that lists every role on hover or keyboard focus.
//
// The list is a portal on `position: fixed` anchored to the chip's
// getBoundingClientRect: the table sits in a `table-scroll` box, which
// clips a plain absolute panel. Scrolling hides it rather than chasing the
// anchor, same as the pending-review popover.
const ROLE_CHIPS_SHOWN = 2;
const roleChipCls = 'inline-block px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs';

const RoleChips = ({ roles: list }) => {
  const [anchor, setAnchor] = useState(null);
  const shown = list.slice(0, ROLE_CHIPS_SHOWN);
  const hidden = list.slice(ROLE_CHIPS_SHOWN);

  useEffect(() => {
    if (!anchor) return undefined;
    const close = () => setAnchor(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [anchor]);

  const open = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setAnchor({ left: r.left, bottom: r.bottom });
  };

  return (
    <span className="flex flex-wrap items-center gap-1">
      {shown.map(r => <span key={r.key} className={roleChipCls}>{r.name}</span>)}
      {hidden.length > 0 && (
        <span
          className={`${roleChipCls} text-slate-500 cursor-default hover:bg-slate-200 focus:bg-slate-200 focus:outline-none`}
          tabIndex={0}
          title={list.map(r => r.name).join(', ')}
          onMouseEnter={open}
          onFocus={open}
          onMouseLeave={() => setAnchor(null)}
          onBlur={() => setAnchor(null)}
        >
          +{hidden.length} more
        </span>
      )}
      {anchor && createPortal(
        <div
          role="tooltip"
          style={{ position: 'fixed', top: anchor.bottom + 6, left: Math.max(8, anchor.left) }}
          className={tooltipPopupCls}
        >
          {list.map(r => <span key={r.key} className="block whitespace-nowrap">{r.name}</span>)}
        </div>,
        document.body
      )}
    </span>
  );
};

// The New User button lives in the page header (SettingsAccounts), which owns
// showCreate so the button can sit beside the title.
const AdminUsers = ({ showCreate, setShowCreate }) => {
  const { user: me } = useAuth();
  const isMe = (u) => !!me?.email && String(u.email || '').toLowerCase() === String(me.email).toLowerCase();
  const [users, setUsers] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, p, r] = await Promise.all([
        fetchJSON('/api/admin/users'),
        fetchJSON('/api/admin/permissions'),
        fetchJSON('/api/admin/roles'),
      ]);
      setUsers(u.users);
      setAllPermissions(p.permissions);
      setRoles(r.roles || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = (u) => rolesOf(u).map(r => r.name).join(', ');

  useEffect(() => { load(); }, []);

  const handleActivate = async (user) => {
    setActionError(null);
    try {
      await fetchJSON(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: true }),
      });
      load();
    } catch (e) { setActionError(e.message); }
  };

  // Role filter = "carries this role" (any of them), grouped by category in
  // the roles list's order.
  const roleOptions = useMemo(() => {
    const present = new Set();
    users.forEach(u => rolesOf(u).forEach(r => present.add(r.key)));
    const groups = [];
    roles.filter(r => present.has(r.key)).forEach(r => {
      const category = r.category || 'General';
      let g = groups.find(x => x.category === category);
      if (!g) { g = { category, roles: [] }; groups.push(g); }
      g.roles.push(r);
    });
    return groups;
  }, [users, roles]);
  const departments = useMemo(() => distinctValues(users, 'department'), [users]);
  const designations = useMemo(() => distinctValues(users, 'designation'), [users]);

  // Whole list is in memory (one account per person), so filtering stays client
  // side — no debounce, no refetch.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (departmentFilter && u.department !== departmentFilter) return false;
      if (designationFilter && u.designation !== designationFilter) return false;
      if (roleFilter && !rolesOf(u).some(r => r.key === roleFilter)) return false;
      if (statusFilter === 'active' && !u.is_active) return false;
      if (statusFilter === 'inactive' && u.is_active) return false;
      if (!q) return true;
      return [u.email, u.name, u.department, u.designation]
        .some(v => (v || '').toLowerCase().includes(q));
    });
  }, [users, search, departmentFilter, designationFilter, roleFilter, statusFilter]);

  const { rows, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(filtered);

  if (loading) return <div className="text-slate-500 text-sm">Loading…</div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>;

  return (
    <div className="space-y-4">
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{actionError}</div>
      )}
      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search email / name / department / designation…"
              className={`${inputCls} pl-9`}
            />
          </div>
          <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)} className={`${inputCls} sm:w-auto`}>
            <option value="">All departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={designationFilter} onChange={e => setDesignationFilter(e.target.value)} className={`${inputCls} sm:w-auto`}>
            <option value="">All designations</option>
            {designations.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className={`${inputCls} sm:w-auto`}>
            <option value="">All roles</option>
            {roleOptions.map(g => (
              <optgroup key={g.category} label={g.category}>
                {g.roles.map(r => <option key={r.key} value={r.key}>{r.name}</option>)}
              </optgroup>
            ))}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`${inputCls} sm:w-auto`}>
            <option value="">Active and inactive</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          <ResultCount count={total} noun="account" className="ml-auto" />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 table-scroll">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortableHeader label="Email" sortKey="email" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
              <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
              <SortableHeader label="Department" sortKey="department" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
              <SortableHeader label="Designation" sortKey="designation" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
              <SortableHeader label="Roles" sortKey="roles" sortConfig={sortConfig} onSort={requestSort} accessor={roleLabel} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
              <SortableHeader label="Permissions" sortKey="permissions" sortConfig={sortConfig} onSort={requestSort} accessor={r => r.permissions?.length || 0} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
              <SortableHeader label="Active" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
              <SortableHeader label="Last Login" sortKey="last_login" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
              <SortableHeader label="Actions" sortable={false} align="right" className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan="9" className="px-4 py-8 text-center text-slate-500">No accounts found</td>
              </tr>
            )}
            {rows.map(u => {
              const extras = (u.extra_permissions || []).length;
              return (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-900">{u.email}</td>
                  <td className="px-4 py-2.5 text-slate-700">{u.name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-2.5 text-slate-700">{u.department || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-2.5 text-slate-700">{u.designation || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-2.5 text-slate-700">
                    <RoleChips roles={rolesOf(u)} />
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                    {(u.permissions || []).length} perm{(u.permissions || []).length === 1 ? '' : 's'}
                    {extras > 0 && (
                      <span className="ml-1 text-amber-700" title="Granted to this account outside its roles">
                        (+{extras} extra)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {u.is_active ? (
                      <span className="inline-block px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">Active</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">
                    {u.last_login ? formatDateTime(u.last_login) : <span className="text-slate-600">never</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setEditingUser(u)}
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {u.is_active && isMe(u) ? (
                        <LockTip text="You cannot deactivate your own account" align="right">
                          <span className="p-1.5 text-slate-300"><UserX className="w-4 h-4" /></span>
                        </LockTip>
                      ) : u.is_active ? (
                        <button
                          onClick={() => { setActionError(null); setDeactivating(u); }}
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Deactivate"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(u)}
                          className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded"
                          title="Reactivate"
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <TablePager
          page={page}
          setPage={setPage}
          total={total}
          className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50"
        />
      </div>

      {editingUser && (
        <UserEditModal
          user={editingUser}
          allPermissions={allPermissions}
          roles={roles}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); load(); }}
        />
      )}

      {showCreate && (
        <UserCreateModal
          allPermissions={allPermissions}
          roles={roles}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {deactivating && (
        <DeactivateUserModal
          user={deactivating}
          onClose={() => setDeactivating(null)}
          onDeactivated={() => { setDeactivating(null); load(); }}
        />
      )}
    </div>
  );
};

// In-app confirm for deactivating a user (replaces window.confirm).
const DeactivateUserModal = ({ user, onClose, onDeactivated }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const confirmDeactivate = async () => {
    setBusy(true); setError(null);
    try {
      await fetchJSON(`/api/admin/users/${user.id}`, { method: 'DELETE' });
      onDeactivated();
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Deactivate User</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Deactivate <span className="font-semibold">{user.email}</span>? They will lose access until reactivated.
          </p>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded disabled:opacity-50">Cancel</button>
            <button onClick={confirmDeactivate} disabled={busy} className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
              {busy ? 'Deactivating…' : 'Deactivate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Expand a role's default entry into a concrete list of permission keys.
// '*' is the sentinel for "every permission in the DB" (the Admin role).
function expandRoleDefaults(role, allPermissions) {
  const entry = role?.permissions;
  if (entry === '*') return allPermissions.map(p => p.key || `${p.module}:${p.action}`);
  return Array.isArray(entry) ? entry : [];
}

// key → 'via Role A, Role B' for every key the picked roles give. These are
// what the tick-list shows locked.
function lockedByRoles(roles, pickedKeys, allPermissions) {
  const via = {};
  roles.filter(r => pickedKeys.includes(r.key)).forEach(r => {
    expandRoleDefaults(r, allPermissions).forEach(key => {
      via[key] = via[key] ? [...via[key], r.name] : [r.name];
    });
  });
  return Object.fromEntries(Object.entries(via).map(([k, names]) => [k, `via ${names.join(', ')}`]));
}

const ModalHelp = () => (
  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2 mb-1">
    <p>
      <span className="text-red-500">*</span> = required.
      Tick every <strong>role</strong> this person holds — its permissions follow the role, so
      changing a role on Roles &amp; Default Permissions changes them here too. Anything ticked
      below on top of the roles is an <strong>extra</strong> for this account alone.
    </p>
  </div>
);

// Roles grouped by category (the list arrives in category order), each group
// collapsible: the account's own department, General, and any group holding
// a ticked role open by default; a search opens everything that matches.
// One line per role: tick to carry it, expand to preview the permissions it
// gives (the owner has many roles; a name alone does not say what it grants).
const RolePicker = ({ roles, allPermissions, picked, onChange, expandCategory = null }) => {
  // The Admin role (every permission) is given or removed only by an
  // administrator — the server refuses anyone else (ADMIN_ROLE_PROTECTED).
  // admin:settings comes only with the Admin role, so it identifies one.
  const { user } = useAuth();
  const canGrantAdmin = user?.role === 'super_admin' || (user?.permissions || []).includes('admin:settings');
  const [open, setOpen] = useState({});
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState(null); // null until the user touches a header
  const labelOf = (key) => {
    const p = allPermissions.find(x => (x.key || `${x.module}:${x.action}`) === key);
    return p ? (p.label || p.description || key) : key;
  };
  const toggle = (key) => onChange(picked.includes(key) ? picked.filter(k => k !== key) : [...picked, key]);

  const q = query.trim().toLowerCase();
  const groups = useMemo(() => {
    const out = [];
    roles.forEach(r => {
      if (q && !`${r.name} ${r.category || ''}`.toLowerCase().includes(q)) return;
      const category = r.category || 'General';
      let g = out.find(x => x.category === category);
      if (!g) { g = { category, roles: [] }; out.push(g); }
      g.roles.push(r);
    });
    return out;
  }, [roles, q]);

  const defaultOpen = (category, groupRoles) =>
    category === 'General' || category === expandCategory || groupRoles.some(r => picked.includes(r.key));
  const isCollapsed = (g) => {
    if (q) return false;
    if (collapsed && Object.prototype.hasOwnProperty.call(collapsed, g.category)) return collapsed[g.category];
    return !defaultOpen(g.category, g.roles);
  };

  return (
    <div className="border border-slate-200 rounded-lg">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 rounded-t-lg">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search roles…"
          className="w-full px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="max-h-[18rem] overflow-y-auto divide-y divide-slate-100">
        {groups.length === 0 && <p className="px-3 py-3 text-sm text-slate-500">{roles.length === 0 ? 'No roles defined yet.' : `No role matches “${query}”.`}</p>}
        {groups.map(g => {
          const on = g.roles.filter(r => picked.includes(r.key)).length;
          const closed = isCollapsed(g);
          return (
            <div key={g.category}>
              <button
                type="button"
                onClick={() => setCollapsed(c => ({ ...(c || {}), [g.category]: !closed }))}
                className="w-full flex items-center gap-2 px-3 py-2 bg-white text-left sticky top-0 z-10 border-b border-slate-100"
              >
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${closed ? '-rotate-90' : ''}`} />
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{g.category}</span>
                <span className={`text-xs ${on > 0 ? 'text-blue-700 font-medium' : 'text-slate-400'}`}>{on}/{g.roles.length}</span>
              </button>
              {!closed && g.roles.map(r => {
                const keys = expandRoleDefaults(r, allPermissions);
                const isOn = picked.includes(r.key);
                const isOpen = !!open[r.key];
                return (
                  <div key={r.key} className={isOn ? 'bg-blue-50/60' : ''}>
                    <div className="flex items-center gap-2.5 pl-8 pr-3 py-2">
                      <LockTip text={r.permissions === '*' && !canGrantAdmin ? 'Only an administrator can give or remove this role' : null}>
                        <span className="flex items-center gap-2.5 min-w-0">
                          <input
                            type="checkbox"
                            checked={isOn}
                            onChange={() => toggle(r.key)}
                            disabled={r.permissions === '*' && !canGrantAdmin}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0 disabled:opacity-50"
                          />
                          <button
                            type="button"
                            onClick={() => toggle(r.key)}
                            disabled={r.permissions === '*' && !canGrantAdmin}
                            className={`text-sm text-left min-w-0 truncate ${isOn ? 'font-medium text-blue-900' : 'text-slate-800'}`}
                          >
                            {r.name}
                          </button>
                        </span>
                      </LockTip>
                      <span className="flex-1" />
                      <button
                        type="button"
                        onClick={() => setOpen(o => ({ ...o, [r.key]: !o[r.key] }))}
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap"
                        title="Preview the permissions this role gives"
                      >
                        {r.permissions === '*' ? 'All permissions' : `${keys.length} permission${keys.length === 1 ? '' : 's'}`}
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {isOpen && (
                      <ul className="pl-[3.75rem] pr-3 pb-2 text-xs text-slate-600 space-y-0.5">
                        {keys.length === 0 && <li className="text-slate-400 italic">Gives no permissions</li>}
                        {keys.map(k => <li key={k} title={k}>{labelOf(k)}</li>)}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UserEditModal = ({ user, allPermissions, roles, onClose, onSaved }) => {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email);
  const [picked, setPicked] = useState(rolesOf(user).map(r => r.key));
  // Extras only — role-given keys are shown locked, not carried in `selected`.
  const [selected, setSelected] = useState(user.extra_permissions || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  // Email the temporary password went to, once this modal has sent one.
  const [resetSentTo, setResetSentTo] = useState(null);

  const locked = lockedByRoles(roles, picked, allPermissions);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await fetchJSON(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name, email, roles: picked }),
      });
      await fetchJSON(`/api/admin/users/${user.id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: selected.filter(k => !locked[k]) }),
      });
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit ${user.email}`} onClose={onClose}>
      <div className="space-y-4">
        <ModalHelp />
        <Field label="Name">
          <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Email" required>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Roles" required>
          <RolePicker roles={roles} allPermissions={allPermissions} picked={picked} onChange={setPicked} expandCategory={user.department || null} />
        </Field>
        <Field label="Password">
          <div className="border border-slate-200 rounded-lg px-3 py-2.5 bg-slate-50 space-y-2">
            {resetSentTo ? (
              <p className="text-sm text-green-700">
                Temporary password sent to {resetSentTo}
              </p>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmReset(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-100"
              >
                <KeyRound className="w-4 h-4" /> Email a Temporary Password
              </button>
            )}
            <p className="text-xs text-slate-500">
              Passwords are never typed in here. A temporary one is generated and emailed
              to the account holder, who must replace it at their next sign-in.
              {user.must_change_password && !resetSentTo && (
                <> A temporary password is already pending for this account.</>
              )}
            </p>
          </div>
        </Field>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Permissions</label>
          <PermissionCheckboxes allPermissions={allPermissions} selected={selected} onChange={setSelected} locked={locked} />
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
          <button onClick={save} disabled={saving || !email || picked.length === 0} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {confirmReset && (
        <ResetPasswordModal
          user={user}
          onClose={() => setConfirmReset(false)}
          onSent={(sentTo) => { setConfirmReset(false); setResetSentTo(sentTo); }}
        />
      )}
    </Modal>
  );
};

// In-app confirm before a temporary password is generated and emailed. The
// administrator never sees the password — the account holder gets it by mail
// and is forced to choose their own at the next sign-in.
const ResetPasswordModal = ({ user, onClose, onSent }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const data = await fetchJSON(`/api/admin/users/${user.id}/reset-password`, { method: 'POST' });
      onSent(data.sentTo || user.email);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Email a Temporary Password</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Send a new temporary password to <span className="font-semibold">{user.email}</span>?
          </p>
          <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
            <li>Their current password stops working and every signed-in session is ended.</li>
            <li>They must choose a new password as soon as they sign in.</li>
            <li>The password is only in the email — it is not shown here.</li>
          </ul>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded disabled:opacity-50">Cancel</button>
            <button onClick={send} disabled={busy} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Sending…' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserCreateModal = ({ allPermissions, roles, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // Open on the first ordinary role (roles arrive in sort order) so a no-touch
  // "Create" gives a safe baseline rather than Admin.
  const defaultRole = (roles.find(r => !r.is_system) || roles[0] || {}).key || '';
  const [picked, setPicked] = useState(defaultRole ? [defaultRole] : []);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const locked = lockedByRoles(roles, picked, allPermissions);

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      await fetchJSON('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ email, name, roles: picked, permissions: selected.filter(k => !locked[k]) }),
      });
      onCreated();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Create New User" onClose={onClose}>
      <div className="space-y-4">
        <ModalHelp />
        <Field label="Email" required>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Name">
          <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Password">
          <p className="text-xs text-slate-500 border border-slate-200 rounded-lg px-3 py-2.5 bg-slate-50">
            No password is typed in here. A temporary one is generated and emailed to the
            address above; the new user must replace it at their first sign-in.
          </p>
        </Field>
        <Field label="Roles" required>
          <RolePicker roles={roles} allPermissions={allPermissions} picked={picked} onChange={setPicked} />
        </Field>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Permissions</label>
          <PermissionCheckboxes allPermissions={allPermissions} selected={selected} onChange={setSelected} locked={locked} />
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
          <button onClick={create} disabled={saving || !email || picked.length === 0} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Creating…' : 'Create & Email Password'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
    {/* 3xl (not 2xl): the permission list is one row per key — labels must fit on one line. */}
    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

export default AdminUsers;
